import React, { useState, useEffect } from "react";
import { Card, Grid, IconButton, CircularProgress } from "@mui/material";
import { Close } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate, useParams } from "react-router-dom";
import api from "utils/api";
import { toast } from "react-toastify";

function EditBlog() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    short_desc: "",
    long_desc: "",
    image: null,
  });
  const [existingImage, setExistingImage] = useState("");

  // Fetch blog data by ID
  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setFetching(true);
        const response = await api.get(`/blogs/${id}`);

        if (response.data.success) {
          const blog = response.data.data;
          setFormData({
            title: blog.title || "",
            category: blog.category || "",
            short_desc: blog.short_desc || "",
            long_desc: blog.long_desc || "",
            image: null,
          });
          setExistingImage(blog.image || "");
          setPreviewImage(blog.image || null);
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        toast.error(error.response?.data?.message || "Failed to fetch blog data");
        navigate("/blogs");
      } finally {
        setFetching(false);
      }
    };

    if (id) {
      fetchBlogData();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid image file (JPEG, PNG, WEBP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.category.trim()) {
      toast.error("Category is required");
      return;
    }
    if (!formData.short_desc.trim()) {
      toast.error("Short description is required");
      return;
    }
    if (!formData.long_desc.trim()) {
      toast.error("Long description is required");
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title.trim());
      submitData.append("category", formData.category.trim());
      submitData.append("short_desc", formData.short_desc.trim());
      submitData.append("long_desc", formData.long_desc.trim());

      // Only append image if a new file is selected
      if (formData.image) {
        submitData.append("image", formData.image);
      } else {
        // Keep existing image URL
        submitData.append("image", existingImage);
      }

      const response = await api.put(`/blogs/${id}`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Blog updated successfully!");
        navigate("/blogs");
      }
    } catch (error) {
      console.error("Error updating blog:", error);
      toast.error(error.response?.data?.message || "Failed to update blog");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/blogs");
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <ArgonTypography variant="h5" fontWeight="medium">
                  Edit Blog
                </ArgonTypography>
                <IconButton onClick={handleCancel} size="small">
                  <Close />
                </IconButton>
              </ArgonBox>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Title <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="title"
                        placeholder="Enter blog title"
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Category <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="category"
                        placeholder="Enter category"
                        value={formData.category}
                        onChange={handleChange}
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Short Description <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="short_desc"
                        placeholder="Enter short description"
                        value={formData.short_desc}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={3}
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Long Description <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="long_desc"
                        placeholder="Enter long description"
                        value={formData.long_desc}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={6}
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Image
                      </ArgonTypography>
                      <ArgonInput
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={handleFileChange}
                        fullWidth
                        inputProps={{
                          accept: "image/jpeg,image/jpg,image/png,image/webp",
                        }}
                      />
                      <ArgonTypography variant="caption" color="text" fontSize="0.7rem" mt={0.5}>
                        Leave empty to keep existing image
                      </ArgonTypography>
                      {previewImage && (
                        <ArgonBox mt={2}>
                          <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                              width: "200px",
                              height: "150px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                            }}
                          />
                        </ArgonBox>
                      )}
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                      <ArgonButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </ArgonButton>
                      <ArgonButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                      >
                        {loading ? "Updating..." : "Update Blog"}
                      </ArgonButton>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </form>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditBlog;
